import { useQueryClient } from "@tanstack/react-query";
import { FC, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import styled, { css } from "styled-components";

import Text from "@/components/Text";
import useToasts from "@/hooks/useToasts";
import { ImageQueryResource } from "@/types/resources";
import { api } from "@/utils/api";

const Query = styled.div<{ $isEditMode: boolean; $child: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  padding: 12px 20px;
  padding-left: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  border-radius: 4px;
  transition: border-color ease-out 0.2s;
  &:last-child {
    margin-bottom: 0;
  }
  ${({ $isEditMode }) =>
    $isEditMode &&
    css`
      border-color: ${({ theme }) => theme.colors.gray};
    `}
  ${({ $child }) =>
    $child &&
    css`
      background: #fdfdfd;
    `}
`;

const QueryIndex = styled.span<{ $child: boolean }>`
  margin-right: 12px;
  flex-shrink: 0;
  ${({ $child }) =>
    $child &&
    css`
      color: ${({ theme }) => theme.colors.gray};
    `}
`;

const StyledText = styled(Text)`
  line-height: 18px;
  padding-right: 24px;
  display: flex;
  align-items: center;
  width: 100%;
`;

const Input = styled.input`
  display: block;
  line-height: 18px;
  margin-right: 24px;
  flex: 1;
  border: 0;
  padding: 0;
  font-size: 12px;
  font-weight: 700;
  outline: 0;
`;

const Actions = styled.div`
  display: flex;
  flex-shrink: 0;
`;

const ActionsLeft = styled.div`
  border-right: 1px solid ${({ theme }) => theme.colors.lightGray};
  padding-right: 12px;
`;

const ActionsRigt = styled.div`
  display: flex;
  gap: 8px;
  padding-left: 12px;
`;

const Button = styled.button`
  display: flex;
  height: 32px;
  width: 32px;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.grayscaleLight};
  color: ${({ theme }) => theme.colors.gray};
  transition: all ease-out 0.2s;
  &:disabled {
    cursor: not-allowed;
    background-color: ${({ theme }) => theme.colors.grayscaleLight};
    color: ${({ theme }) => theme.colors.gray};
    &:hover {
      background-color: ${({ theme }) => theme.colors.grayscaleLight};
      color: ${({ theme }) => theme.colors.gray};
    }
  }
`;

const AddButton = styled(Button)`
  color: ${({ theme }) => theme.colors.purple};
  background-color: rgba(93, 84, 214, 0.08);
  &:hover,
  &:active {
    background-color: rgba(93, 84, 214, 0.14);
  }
`;

const DeleteButton = styled(Button)`
  &:hover,
  &:active {
    color: ${({ theme }) => theme.colors.error};
    background-color: ${({ theme }) => theme.colors.errorLight};
  }
`;

const EditButton = styled(Button)`
  &:hover,
  &:active {
    color: ${({ theme }) => theme.colors.black};
    background-color: ${({ theme }) => theme.colors.lightGray};
  }
`;

const ConfirmEditButton = styled(Button)`
  color: ${({ theme }) => theme.colors.success};
  background-color: ${({ theme }) => theme.colors.successLight};
  &:hover,
  &:active {
    background-color: #ddf3e6;
  }
`;

interface Props {
  query: ImageQueryResource;
  index: number;
  childIndex?: number;
  child?: boolean;
}

const ImageToolQuery: FC<Props> = ({ query, index, child, childIndex }) => {
  const { id } = useParams();

  const [isEditMode, setIsEditMode] = useState(false);

  const [isGeneratingQueries, setIsGeneratingQueries] = useState(false);

  const queryClient = useQueryClient();

  const inputRef = useRef<HTMLInputElement>(null);

  const toasts = useToasts();

  const onDelete = async () => {
    const toastId = toasts.loading();

    try {
      await api.delete(`/v1/image-queries/${query.id}`);

      await queryClient.invalidateQueries(
        ["image_generation_project", { id }],
        {
          exact: true,
        }
      );

      toasts.success(toastId);
    } catch (_) {
      toasts.error(toastId);
    }
  };

  const onUpdate = async () => {
    const toastId = toasts.loading();

    try {
      const newValue = inputRef.current?.value;

      if (!newValue || newValue?.length < 1) {
        setIsEditMode(false);

        return toasts.error(toastId);
      }

      await api.put(`/v1/image-queries/${query.id}`, {
        newValue,
      });

      await queryClient.invalidateQueries(
        ["image_generation_project", { id }],
        {
          exact: true,
        }
      );

      setIsEditMode(false);

      toasts.success(toastId);
    } catch (_) {
      toasts.error(toastId);
    }
  };

  const onGenerateQueries = async () => {
    const toastId = toasts.loading();

    setIsGeneratingQueries(true);

    try {
      await api.post(`/v1/image-queries/${query.id}/generate`);

      await queryClient.invalidateQueries(
        ["image_generation_project", { id }],
        {
          exact: true,
        }
      );

      toasts.success(toastId);
    } catch (_) {
      toasts.error(toastId);
    }

    setIsGeneratingQueries(false);
  };

  return (
    <Query $isEditMode={isEditMode} $child={!!child}>
      <StyledText $type="smallBold">
        <QueryIndex $child={!!child}>
          {index + 1}
          {!!childIndex && `-${childIndex}`}.
        </QueryIndex>
        {isEditMode ? (
          <Input ref={inputRef} autoFocus defaultValue={query.name} />
        ) : (
          query.name
        )}
      </StyledText>
      <Actions>
        {!isEditMode && !child && (
          <ActionsLeft>
            <AddButton
              disabled={isGeneratingQueries}
              onClick={onGenerateQueries}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M8.0006 2.34315V13.6569"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M13.6575 8H2.34375"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </AddButton>
          </ActionsLeft>
        )}
        <ActionsRigt>
          {isEditMode ? (
            <>
              <ConfirmEditButton onClick={onUpdate}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13.5 4.5L6.5 11.4997L3 8"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </ConfirmEditButton>
              <DeleteButton onClick={() => setIsEditMode(false)}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 4L4 12"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 12L4 4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </DeleteButton>
            </>
          ) : (
            <>
              <EditButton
                disabled={isGeneratingQueries}
                onClick={() => setIsEditMode(true)}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.67489 14.1176H2.42454C2.28086 14.1176 2.14307 14.0605 2.04148 13.959C1.93989 13.8574 1.88281 13.7196 1.88281 13.5759V10.5499C1.88281 10.4788 1.89682 10.4083 1.92405 10.3426C1.95127 10.2769 1.99118 10.2172 2.04148 10.1669L10.1674 2.04099C10.269 1.9394 10.4067 1.88232 10.5504 1.88232C10.6941 1.88232 10.8319 1.9394 10.9335 2.04099L13.9594 5.06695C14.061 5.16855 14.1181 5.30634 14.1181 5.45001C14.1181 5.59369 14.061 5.73148 13.9594 5.83307L5.67489 14.1176Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M7.38477 4.92285L11.1495 8.61226"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </EditButton>
              <DeleteButton disabled={isGeneratingQueries} onClick={onDelete}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 4L4 12"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 12L4 4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </DeleteButton>
            </>
          )}
        </ActionsRigt>
      </Actions>
    </Query>
  );
};

export default ImageToolQuery;
